import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { throwCoins, findHexagram, getActiveLineText } from '../lib/coinToss';
import hexagrams from '../data/hexagrams.json';

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
`;

const Title = styled.h3`
  color: #a78bfa;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 16px 0;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const spin = keyframes`
  0% { transform: rotateY(0deg); }
  100% { transform: rotateY(360deg); }
`;

const CoinRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 20px;
`;

const Coin = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  animation: ${({ $spinning }) => $spinning ? spin : 'none'} 0.5s linear;
  box-shadow: 0 2px 8px rgba(245,158,11,0.3);
`;

const LinesArea = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 6px;
  align-items: center;
  margin-bottom: 20px;
  min-height: 120px;
`;

const LineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LineSymbol = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const LineBar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${({ $yang }) => $yang ? '#f59e0b' : '#6b7280'};
  width: ${({ $yang }) => $yang ? '52px' : '22px'};
`;

const LineLabel = styled.span`
  color: #6b7280;
  font-size: 11px;
  width: 30px;
`;

const LineChanging = styled.span`
  color: #ef4444;
  font-size: 10px;
  font-weight: 700;
`;

const TossBtn = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ProgressText = styled.div`
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  margin-bottom: 12px;
`;

function LineDisplay({ lineValue, position }) {
  const isYang = lineValue === 7 || lineValue === 9;
  const isChanging = lineValue === 6 || lineValue === 9;

  return (
    <LineRow>
      <LineLabel>{position}효</LineLabel>
      <LineSymbol>
        {isYang ? (
          <LineBar $yang />
        ) : (
          <>
            <LineBar />
            <LineBar />
          </>
        )}
      </LineSymbol>
      {isChanging && <LineChanging>변</LineChanging>}
    </LineRow>
  );
}

export default function CoinToss({ onComplete }) {
  const [lines, setLines] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [coinValues, setCoinValues] = useState([null, null, null]);
  const [done, setDone] = useState(false);

  const tossOneLine = useCallback(async () => {
    if (isAnimating || done) return;

    setIsAnimating(true);
    // 동전 애니메이션
    await new Promise(r => setTimeout(r, 50));
    const result = throwCoins();

    // 동전 개별 값 표시 (3 또는 2)
    const c1 = Math.random() < 0.5 ? 3 : 2;
    const c2 = Math.random() < 0.5 ? 3 : 2;
    const c3 = result - c1 - c2 < 2 ? result - c1 - 2 : result - c1 - c2;
    setCoinValues([c1, c2, Math.max(2, Math.min(3, result - c1 - c2 < 0 ? 2 : result - c1 - (c1 + c2 === result ? c2 : 2)))]);

    await new Promise(r => setTimeout(r, 450));

    const newLines = [...lines, result];
    setLines(newLines);
    setIsAnimating(false);

    if (newLines.length === 6) {
      setDone(true);
      const binary = newLines.map(v => (v === 7 || v === 9) ? '1' : '0').join('');
      const changingLines = newLines.map((v, i) => (v === 6 || v === 9) ? i + 1 : null).filter(Boolean);
      const changedBinary = newLines.map(v => {
        if (v === 6) return '1';
        if (v === 9) return '0';
        return (v === 7) ? '1' : '0';
      }).join('');

      const gua = Object.values(hexagrams).find(h => h.binary === binary);
      const changedGua = changingLines.length > 0
        ? Object.values(hexagrams).find(h => h.binary === changedBinary)
        : null;
      const activeLine = gua && changingLines.length > 0
        ? getActiveLineText(gua, changingLines)
        : null;

      setTimeout(() => onComplete({ gua, changedGua, changingLines, activeLine }), 600);
    }
  }, [isAnimating, done, lines, onComplete]);

  const reset = () => {
    setLines([]);
    setCoinValues([null, null, null]);
    setDone(false);
    setIsAnimating(false);
  };

  return (
    <Card>
      <Title>Step 3 — 동전 던지기</Title>

      <CoinRow>
        {[0, 1, 2].map(i => (
          <Coin key={i} $spinning={isAnimating}>
            {coinValues[i] === 3 ? '앞' : coinValues[i] === 2 ? '뒤' : '☯'}
          </Coin>
        ))}
      </CoinRow>

      {lines.length > 0 && (
        <LinesArea>
          {lines.map((v, i) => (
            <LineDisplay key={i} lineValue={v} position={i + 1} />
          ))}
        </LinesArea>
      )}

      <ProgressText>
        {done ? '6효 완성' : `${lines.length} / 6 효`}
      </ProgressText>

      {!done && (
        <TossBtn onClick={tossOneLine} disabled={isAnimating}>
          {isAnimating ? '던지는 중...' : lines.length === 0 ? '동전 던지기 시작' : `${lines.length + 1}번째 효 던지기`}
        </TossBtn>
      )}

      {done && (
        <TossBtn onClick={reset} style={{ background: 'linear-gradient(135deg,#374151,#1f2937)' }}>
          다시 던지기
        </TossBtn>
      )}
    </Card>
  );
}
